import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Tenants } from '@/lib/api/v1';
import { useSession, usePrefs } from '@/lib/store';
import { Icon } from '@/design-system';
import { clsx } from 'clsx';

export function HouseholdSwitcher() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, memberships } = useSession();
  const { activeTenantId, setActiveTenantId } = usePrefs();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click + Escape — prevents the dropdown from "stealing"
  // the next sidebar nav click underneath it.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const tenantsQ = useQuery({
    queryKey: ['tenants'],
    queryFn: () => Tenants.list(),
    enabled: Boolean(user),
    staleTime: 60_000,
  });

  if (!user) return null;
  const tenants = tenantsQ.data?.tenants ?? [];
  const active = tenants.find((t) => t.id === activeTenantId) ?? tenants[0];

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="ui-card flex w-full items-center gap-3 p-3 text-left transition-colors hover:border-line-soft"
      >
        <div className="grid h-9 w-9 place-items-center rounded-full bg-candle/10 text-candle">
          <Icon name="container" size={16} />
        </div>
        <div className="grid flex-1 min-w-0">
          <span className="text-xs uppercase tracking-widest text-mist">Husholdning</span>
          <span className="truncate text-sm font-semibold text-parchment">
            {active?.name ?? 'Ingen valgt'}
          </span>
        </div>
        <Icon name="chevron-down" size={14} className={clsx('text-mist transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          role="listbox"
          /* High z-index + opaque background + heavier shadow so the dropdown
             does NOT visually blend with the nav links underneath. */
          className="absolute left-0 right-0 top-full z-[60] mt-2 grid gap-1 rounded-[var(--ui-radius-lg)] border border-line bg-ink-soft p-2 shadow-elevated"
          style={{ backgroundColor: 'var(--ui-color-ink-soft)' }}
        >
          {tenants.length === 0 && (
            <div className="px-3 py-2 text-xs text-mist">Ingen husholdninger</div>
          )}
          {tenants.map((tn) => {
            const ms = memberships.find((m) => m.tenantId === tn.id);
            return (
              <button
                key={tn.id}
                type="button"
                role="option"
                aria-selected={tn.id === active?.id}
                onClick={() => {
                  setActiveTenantId(tn.id);
                  setOpen(false);
                  void qc.invalidateQueries();
                }}
                className={clsx(
                  'flex items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                  tn.id === active?.id ? 'bg-candle/10 text-candle' : 'text-parchment hover:bg-overlay-soft',
                )}
              >
                <span className="truncate">{tn.name}</span>
                {ms && (
                  <span className="ui-badge ui-badge--mist text-[9px]">{ms.role}</span>
                )}
              </button>
            );
          })}
          <div className="my-1 h-px bg-line" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/onboarding/create-household');
            }}
            className="flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-candle hover:bg-candle/10"
          >
            <Icon name="plus" size={14} /> Ny husholdning
          </button>
        </div>
      )}
    </div>
  );
}
